import { useState, useEffect } from 'react';
import { MenuItem, TextField, AppBar, Button, Tooltip, Toolbar, Modal, ListItemIcon, Menu, ListItemText, Box, TableContainer, Table, TableCell, TableBody, TableHead, TableRow, useTheme, IconButton, Divider, alpha, } from '@mui/material';
import dayjs from 'dayjs';
import * as XLSX from "xlsx-js-style"; // <-- important: use xlsx-js-style for styling support
import { saveAs } from "file-saver";
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import ListAltIcon from '@mui/icons-material/ListAlt';
import RefreshIcon from '@mui/icons-material/Refresh';
import Close from '@mui/icons-material/Close';
import HelpIcon from '@mui/icons-material/HelpOutline';
import Searchicon from '@mui/icons-material/Search';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import FormatAlignJustifyIcon from '@mui/icons-material/FormatAlignJustify';

import CustomSnackbar from '../../../components/CustomSnackbar';
import GlobalConfig from '../../../components/GlobalConfig';
import { tokens } from "../../../theme/theme";

import CreateNewInvoice from './CreateNewInvoice';
import CustomTableContainer from '../../../components/table/CustomTableContainer';
import ResponsiveBox from '../../../components/form/ResponsiveScrollBox';
import DateRangePicker from '../../../components/DateRangePicker';
import { useInvoiceOrderList } from '../../../hooks/salesorder/useInvoiceOrderList';
import { DateRangePickerDay } from '@mui/x-date-pickers-pro';
import DateRangePickerForInvoice from '../../../components/DateRangePickerForInvoice';
import TableFilterRow from '../../../components/table/TableFilterRow';
import CustomPagination from '../../../components/table/CustomPagination';
import { Description, DescriptionOutlined, Edit, EditAttributes, FileDownloadOutlined, Print, PrintOutlined } from '@mui/icons-material';
import { callApi } from '../../../utils/apiClient';
import { API_ENDPOINTS } from '../../../config/api/apiCall';
import PackingListReport from './PackingListReport';
import useDefaultParameterConfigById from '../../../hooks/useDefaultParameterConfigById';
import InvoiceReport from './InvoiceReport';



const InvoiceList = ({ handleBackWinderMachineEntry }) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const [fromData, setFromDate] = useState(dayjs().subtract(GlobalConfig.defaultDays, 'day').format('DD/MMM/YYYY'));
    const [toDate, setToDate] = useState(dayjs().format('DD/MMM/YYYY'));
    const [isDataLoad, setIsDataLoad] = useState(false);

    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');
    const [alertsevierity, setalertsevierity] = useState('');

    const showError = (message, severity) => {
        setAlertMessage(message);
        setalertsevierity(severity);
        showSnackbar();
        return true;
    };

    const [selectedStatus, setSelectedStatus] = useState('All');
    const [selectedRows, setSelectedRows] = useState([]);

    const [anchorElFirst, setAnchorElFirst] = useState(null);
    const [anchorElSecond, setAnchorElSecond] = useState(null);
    const openFirst = Boolean(anchorElFirst);
    const openSecond = Boolean(anchorElSecond);
    const [selectedOptionFirst, setSelectedOptionFirst] = useState('All Invoice');

    const [mode, setMode] = useState('list'); // 'list' | 'create'
    const [openInvoice, setOpenInvoice] = useState(false);

    const { tableData, loading, loadInvoiceOrderList } = useInvoiceOrderList(showError);

    // 🔥 NEW search fields
    const [searchType, setSearchType] = useState("DO No");
    const [searchValue, setSearchValue] = useState("");

    const onSearchTypeChange = (e) => {
        setSearchType(e.target.value);
    };

    const onSearchValueChange = (e) => {
        setSearchValue(e.target.value);
    };

    const onSearchClick = async () => {
        await loadInvoiceOrderList(fromData, toDate, searchType, searchValue);
    };


    const handleBackDashboard = () => {
        handleBackWinderMachineEntry();
    }

    const showSnackbar = () => {
        setSnackbarOpen(true);
    };

    // single reusable function
    const handleDateChange = async (date, type) => {
        if (type === "from") {
            setFromDate(dayjs(date));
        } else {
            setToDate(dayjs(date));
        }

        // call API with updated dates
        await loadInvoiceOrderList(
            type === "from" ? dayjs(date) : fromData,
            type === "to" ? dayjs(date) : toDate,
            searchType,   // 🔥 pass current searchType
            searchValue   // 🔥 pass current searchValue
        );
    };


    useEffect(() => {
        if (!isDataLoad) {
            loadInvoiceOrderList(fromData, toDate);
            setIsDataLoad(true);
        }
    }, [])


    const handleCreateDispatchPlans = () => {
        setMode('create');
        setSelectedRows([]);
    };

    const handleSnackbarClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setSnackbarOpen(false);
    };
    const [filters, setFilters] = useState({
        InvoiceNo: '',
        InvoiceStatus: '',
        BillToCustomer: '',
        ShipToCustomer: '',
        ShipToCity: '',
        ShipToState: '',
        ConfirmationNo: '',
        ShippedOrderNo: '',
        IRNNo: '',
    });

    const filteredData_Invoice = tableData.filter((row) => {
        // Check if row matches selected status
        if (selectedStatus === 'All' || row.InvoiceStatus === selectedStatus) {
            // Check if all filter fields match
            return Object.keys(filters).every((key) => {
                if (filters[key] === '') return true; // No filter applied
                return String(row[key]).toLowerCase().includes(filters[key].toLowerCase());
            });
        }
        return false;
    });

    const handleOpen_Invoice = () => {
        setOpenInvoice(true);
    };

    const handleClose_Invoice = () => {
        setOpenInvoice(false);
    };

    const handleMenuClickFirst = (event) => {
        setAnchorElFirst(event.currentTarget);
    };

    const handleMenuCloseFirst = () => {
        setAnchorElFirst(null);
    };

    const handleMenuItemClickFirst = (option) => {
        setSelectedOptionFirst(option); // Update the selected option
        handleMenuCloseFirst();

        if (option === 'New') {
            setSelectedStatus('New');
        }
        else if (option === 'Approved') {
            setSelectedStatus('Approved');
        }
        else if (option === 'Cancelled') {
            setSelectedStatus('Cancelled');
        }
        else if (option === 'Under Amendment') {
            setSelectedStatus('Under Amendment');
        }
        else if (option === 'Processed') {
            setSelectedStatus('Processed');
        }
        else if (option === 'Invoiced') {
            setSelectedStatus('Invoiced');
        }

        else if (option === 'All Invoice') {
            setSelectedStatus('All');
        }
        setAnchorElFirst(null);
    };

    // Handlers for second menu
    const handleMenuClickSecond = (event) => {
        setAnchorElSecond(event.currentTarget);
    };

    const handleMenuCloseSecond = () => {
        setAnchorElSecond(null);
    };

    const handleMenuItemClickSecond = (option) => {
        handleMenuCloseSecond();
    };



    const handleCallEdit = (rows) => {
        setMode('create');
        setSelectedRows(rows);
    };

    const handleCallBack = () => {
        setMode('list');
        setSelectedRows([]);
    };

    const [recordsPerPage, setRecordsPerPage] = useState(20); // Records per page
    const [currentPage, setCurrentPage] = useState(1);


    const handleFilterChange = (e, column) => {
        setFilters({ ...filters, [column]: e.target.value });
    };

    const handlePageChange = (event, value) => {
        setCurrentPage(value);
    };

    const pageCount = Math.ceil(filteredData_Invoice.length / recordsPerPage);

    const uniqueValues = {
        InvoiceNo: [...new Set(tableData.map(row => row.InvoiceNo))],
        InvoiceStatus: [...new Set(tableData.map(row => row.InvoiceStatus))],
        BillToCustomer: [...new Set(tableData.map(row => row.BillToCustomer))],
        ShipToCustomer: [...new Set(tableData.map(row => row.ShipToCustomer))],
        ShipToCity: [...new Set(tableData.map(row => row.ShipToCity))],
        ShipToState: [...new Set(tableData.map(row => row.ShipToState))],
        ConfirmationNo: [...new Set(tableData.map(row => row.ConfirmationNo))],
        ShippedOrderNo: [...new Set(tableData.map(row => row.ShippedOrderNo))],
        IRNNo: [...new Set(tableData.map(row => row.IRNNo))],
    };


    // Get the records for the current page
    const currentRecords = filteredData_Invoice.slice(
        (currentPage - 1) * recordsPerPage,
        currentPage * recordsPerPage
    );


    const autoCompleteColumns = ['InvoiceNo', 'InvoiceStatus', 'BillToCustomer', 'ShipToCustomer', 'ShipToCity', 'ShipToState', 'ConfirmationNo', 'ShippedOrderNo', 'IRNNo'];

    const columns = ['', 'InvoiceNo', 'InvoiceDate', 'InvoiceStatus', 'BillToCustomer', 'ShipToCustomer', 'ShipToCity', 'ShipToState', 'ConfirmationNo', 'TotalQty', 'ShippedOrderNo', 'ShippedOrderDate', 'NetAmount', 'IRNNo', 'InvoicePlanNo'];

    const totals =
    {
        TotalQty: filteredData_Invoice.reduce((sum, r) => sum + (parseFloat(r.TotalQty) || 0), 0),
        NetAmount: filteredData_Invoice.reduce((sum, r) => sum + (parseFloat(r.NetAmount) || 0), 0),
    };

    let [FinancierYear, setFinancierYear] = useState(1);
    const [PackingList, setPackingList] = useState([]);
    const [InvoiceList, setInvoiceList] = useState([]);
    const [viewPackingList, setViewPackingList] = useState(false);
    const [viewInvoiceReport, setViewInvoiceReport] = useState(false);
    const [viewInvoiceDetail, setInvoiceDetail] = useState(true);
    const [rowsConfirmedOrdersDespacth, setrowsConfirmedOrdersDespacth] = useState([]);
    const [taxMode, setTaxMode] = useState(0);
    const { defaultParameterConfig, setDefaultParameterConfig, isDataLoaded, setIsDataLoaded, getParemeterDetail } = useDefaultParameterConfigById(14);
    const [headerData, setHeaderData] = useState([]);
    const [manualTCSRateEdit, setManualTCSRateEdit] = useState(0);

    const handleInvoiceExport = async (IParentInvoiceOrder_ID) => {

        try {
            const payload = {
                p_companyid: localStorage.getItem("companyid"),
                p_finyearid: FinancierYear,
                p_parentinvoiceorderid: IParentInvoiceOrder_ID,
            };

            const endpoint = API_ENDPOINTS.DownloadPackingListExcelReport;
            const response = await callApi(
                endpoint,
                "POST",
                { "Content-Type": "application/json" },
                payload
            );

            if (!response || response.length === 0) {
                alert("No packing list data found!");
                return;
            }

            // ✅ Rename columns
            const columnMap = {
                OrderNo: "Order No",
                BundleRefNo: "Bundle Ref. No",
                PO: "PO",
                ItemNo: "ItemNo",
                SLoc: "SLoc",
                Batch: "Batch",
                BundleNo: "Bundle",
                ReamWt: "Ream wt.",
                NoOfReams: "No Of Reams",
                GrWt: "Gr Wt.",
                NetQty: "Net Qty",
                UOM: "UOM",
                VendorInvoiceNo: "Vendor Invoice No",
            };

            const renamedData = response.map((item) => {
                const renamedItem = {};
                for (const key in item) {
                    renamedItem[columnMap[key] || key] = item[key];
                }
                return renamedItem;
            });

            // ✅ Get header names
            const headers = Object.keys(renamedData[0]);

            // ✅ Create empty worksheet first
            const worksheet = XLSX.utils.book_new();

            // ✅ Add only data rows (without headers)
            const dataRows = renamedData.map((obj) => headers.map((h) => obj[h]));
            const ws = XLSX.utils.aoa_to_sheet([]);

            // ✅ Add styled header row manually
            const headerRow = headers.map((h) => ({
                v: h,
                s: {
                    font: { bold: true, color: { rgb: "000000" } },
                    fill: { fgColor: { rgb: "D9D9D9" } }, // light grey
                    alignment: { horizontal: "center", vertical: "center" },
                    border: {
                        top: { style: "thin", color: { rgb: "000000" } },
                        bottom: { style: "thin", color: { rgb: "000000" } },
                        left: { style: "thin", color: { rgb: "000000" } },
                        right: { style: "thin", color: { rgb: "000000" } },
                    },
                },
            }));

            XLSX.utils.sheet_add_aoa(ws, [headerRow], { origin: "A1" });

            // ✅ Add actual data starting from A2
            XLSX.utils.sheet_add_aoa(ws, dataRows, { origin: "A2" });

            // ✅ Auto column width
            const colWidths = headers.map((header) => {
                const maxLength = Math.max(
                    header.length,
                    ...renamedData.map((r) => (r[header] ? r[header].toString().length : 0))
                );
                return { wch: maxLength + 2 };
            });
            ws["!cols"] = colWidths;

            // ✅ Create workbook and export
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, ws, "PackingList");

            const excelBuffer = XLSX.write(workbook, {
                bookType: "xlsx",
                type: "array",
            });

            const blob = new Blob([excelBuffer], {
                type: "application/octet-stream",
            });

            saveAs(blob, "PackingList.xlsx");

        } catch (error) {
            console.error("Error fetching packing list:", error);
            alert("Failed to fetch packing list data!");
        }

    }

    const [totalInvoice, setTotalInvoice] = useState({
        TotalQty: 0,
        totalAmountExclDiscount: 0,
        totalDiscount: 0,
        totalAmountAfterDiscount: 0,
        freightAmount: 0,
        insuranceAmount: 0,
        serviceChargeAmount: 0,
        totalTaxableAmount: 0,
        totalGSTAmount: 0,
        totalCessAmount: 0,
        roundOff: 0,
        tcsAmount: 0,
        netAmountWithRounding: 0,
        salesAmount: 0,
        cgstRate: 0,
        sgstRate: 0,
        igstRate: 0,
        discountRate: 0,
        discountType: 'Flat',
        freightRate: 0,
        insurancePercentage: 0,
        serviceCharge: 0,
        tcsPercentage: 0,
        igstAmount: 0,
        cgstAmount: 0,
        sgstAmount: 0,
        cessRate: 0,

    });


    const handleOrderItemCalculation = async (header, items) => {

        let totalQty = 0;
        let totalAmountExclDiscount = 0;
        let totalAmountAfterDiscount = 0;
        let totalDiscount = 0;
        let totalTaxAmount = 0;

        let cessRate = header.CessRate;
        let freightRate = header.FreightPerMT;
        let insuranceRate = header.insuranceRatePct || 0;
        let serviceChargeRate = header.ServiceChargeRate;

        // let cessRate = 1;
        // let freightRate = 1;
        // let insuranceRate = 1;
        // let serviceChargeRate = 1;

        // const originState = supplierAddr.find(
        //     (x) => x.LabelName?.toLowerCase() === "state"
        // )?.LabelValue?.toLowerCase();

        // const destinationState = billToAddr.find(
        //     (x) => x.LabelName?.toLowerCase() === "state"
        // )?.LabelValue?.toLowerCase();

        let isInterState = taxMode; //taxMode;//originState !== destinationState;
        isInterState = taxMode === 0 ? true : false;

        const taxBreakdown = [];
        let discountType = 'rate';
        let discountValue = 0;

        let isExportOrder = false;
        let isDomesticOrder = false;

        const round2 = (num) =>
            Number(Number(num).toFixed(2));


        const rows = items.map((row) => {

            let lineQty = row.SIRequested_Qty || 0;
            if (row.SIConfirmed_Qty > 0) lineQty = row.SIConfirmed_Qty;
            if (row.SIPlanned_Qty > 0) lineQty = row.SIPlanned_Qty;
            if (row.SIShipped_Qty > 0) lineQty = row.SIShipped_Qty;

            const lineUnitPrice = row.SIUnitPrice || 0;
            //discountValue = row.SIDiscount || 0;
            //const discountValue = 0;
            discountType = row.SIDiscountType?.toLowerCase();
            let discountedUnitPrice = lineUnitPrice;
            let lineDiscountAmt = 0;

            if (discountValue > 0) {
                if (discountType === "rate") {
                    discountedUnitPrice = lineUnitPrice - (lineUnitPrice * discountValue) / 100;
                    lineDiscountAmt = lineQty * ((lineUnitPrice * discountValue) / 100);
                } else {
                    discountedUnitPrice = lineUnitPrice - discountValue;
                    lineDiscountAmt = lineQty * discountValue;
                }
            }

            const lineAmount = round2(lineQty * lineUnitPrice);
            lineDiscountAmt = round2(lineDiscountAmt);
            const afterDiscount = round2(lineAmount - lineDiscountAmt);

            // Convert quantity to kg
            if (row.SIUnit?.toLowerCase() === "ton") lineQty *= 1000;

            const insuranceAmt = round2((afterDiscount * insuranceRate) / 100);
            const freightAmt = round2((lineQty / 1000) * freightRate);
            const serviceChargeAmt = round2((afterDiscount * serviceChargeRate) / 100);
            const taxableValue = round2(afterDiscount + insuranceAmt + serviceChargeAmt);

            let cgstAmt = 0, sgstAmt = 0, igstAmt = 0;

            if (isInterState) {
                if (row.SIIGSTPercent > 0) {
                    igstAmt = round2((taxableValue * row.SIIGSTPercent) / 100);
                    taxBreakdown.push({
                        TaxCategory: "IGST Tax",
                        TaxPercent: row.SIIGSTPercent,
                        TaxableAmount: taxableValue,
                        TaxAmount: igstAmt,
                    });
                }
            } else {
                if (row.SICGSTPercent > 0) {
                    cgstAmt = round2((taxableValue * row.SICGSTPercent) / 100);
                    taxBreakdown.push({
                        TaxCategory: "CGST Tax",
                        TaxPercent: row.SICGSTPercent,
                        TaxableAmount: taxableValue,
                        TaxAmount: cgstAmt,
                    });
                }
                if (row.SISGSTPercent > 0) {
                    sgstAmt = round2((taxableValue * row.SISGSTPercent) / 100);
                    taxBreakdown.push({
                        TaxCategory: "SGST Tax",
                        TaxPercent: row.SISGSTPercent,
                        TaxableAmount: taxableValue,
                        TaxAmount: sgstAmt,
                    });
                }
            }

            const lineTaxAmount = isInterState ? igstAmt : cgstAmt + sgstAmt;
            const cessAmt = round2((lineTaxAmount * cessRate) / 100);

            const totalLineAmount = round2(
                afterDiscount + insuranceAmt + serviceChargeAmt + cessAmt + lineTaxAmount
            );

            totalQty += lineQty;
            totalAmountExclDiscount += lineAmount;
            totalTaxAmount += lineTaxAmount;
            totalDiscount += lineDiscountAmt;
            totalAmountAfterDiscount += afterDiscount;



            return {
                ...row,
                SILineTaxableAmount: taxableValue,
                SIDiscountAmount: lineDiscountAmt,
                SICGSTAmount: cgstAmt,
                SISGSTAmount: sgstAmt,
                SIIGSTAmount: igstAmt,
                SICessAmount: cessAmt,
                SIFreightAmount: freightAmt,
                SIInsuranceAmount: insuranceAmt,
                SIServiceChargeAmount: serviceChargeAmt,
                SIBeforeDiscountAmount: lineAmount,
                SIAfterDiscountAmount: afterDiscount,
                SILineTaxAmount: totalLineAmount,
            };
        });


        // Fright, Insurance, Service charge amount caluculations
        const freightAmount = round2((totalQty / 1000) * freightRate);

        const insuranceAmount = round2(
            ((freightAmount + totalAmountAfterDiscount) * insuranceRate) / 100
        );

        const serviceChargeAmount = round2(
            (totalAmountAfterDiscount * serviceChargeRate) / 100
        );

        const totalTaxableAmount = round2(
            totalAmountAfterDiscount + insuranceAmount + freightAmount + serviceChargeAmount
        );

        // gst summary calculations
        let cgstRate = 0, sgstRate = 0, igstRate = 0;
        let cgstAmount = 0, sgstAmount = 0, igstAmount = 0;

        if (!isExportOrder) {
            if (isInterState) {

                igstRate = items[0].SIIGSTPercent || 0;
                igstAmount = round2((totalTaxableAmount * igstRate) / 100);
                taxBreakdown.push({
                    TaxCategory: "IGST Tax",
                    TaxPercent: igstRate,
                    TaxableAmount: totalTaxableAmount,
                    TaxAmount: igstAmount,
                });
            } else {
                cgstRate = items[0]?.SICGSTPercent || 0;
                sgstRate = items[0]?.SISGSTPercent || 0;
                cgstAmount = round2((totalTaxableAmount * cgstRate) / 100);
                sgstAmount = round2((totalTaxableAmount * sgstRate) / 100);
                taxBreakdown.push(
                    {
                        TaxCategory: "CGST Tax",
                        TaxPercent: cgstRate,
                        TaxableAmount: totalTaxableAmount,
                        TaxAmount: cgstAmount,
                    },
                    {
                        TaxCategory: "SGST Tax",
                        TaxPercent: sgstRate,
                        TaxableAmount: totalTaxableAmount,
                        TaxAmount: sgstAmount,
                    }
                );
            }
        }

        const totalGSTAmount = round2(igstAmount + cgstAmount + sgstAmount);

        // cess calculations
        const totalCessAmount = round2((totalGSTAmount * cessRate) / 100);

        // net amount calculations
        let netAmountWithoutRounding = round2(totalAmountAfterDiscount + insuranceAmount + freightAmount + serviceChargeAmount + totalCessAmount + totalGSTAmount);
        let netAmountWithRounding = Math.round(netAmountWithoutRounding);
        const roundOff = round2(netAmountWithRounding - netAmountWithoutRounding);
        let salesAmount = netAmountWithRounding;

        // get tcs value 
        let tcsData = await getParemeterDetail(14);

        // TCS calculations
        let tcsAmount = 0;
        let bTCSApplicable = false;
        let mTCSRate = 0;
        let mTCSThresoldAmount = 0;
        let bManualTCSCalculation = true;
        let bPartyTCSControlledByFlag = true;
        let tcsRate = 0;
        let bPartyTCSApplicable = false;
        let mCurrentSaleValue = headerData.CurrentSaleValue;
        let mOrderStatus = 'Invoiced';

        if (defaultParameterConfig[0]?.p_default_value_code === '1') {
            bTCSApplicable = true;
            mTCSRate = parseFloat(defaultParameterConfig[0].p_minvalue);
            mTCSThresoldAmount = parseFloat(defaultParameterConfig[0].p_maxvalue);
            bManualTCSCalculation = parseFloat(defaultParameterConfig[0].p_enableui);
            bPartyTCSControlledByFlag = (defaultParameterConfig[0].p_default_value);

        }

        setManualTCSRateEdit(bManualTCSCalculation);

        const oTCSRate = Number(tcsRate || 0);          // UI supplied TCS rate (oTCSRate)
        const manualTCS = !!bManualTCSCalculation;      // boolean
        const partyControlFlag = !!bPartyTCSControlledByFlag; // boolean
        const partyTCSApplicable = !!bPartyTCSApplicable;     // boolean (fallback false if undefined)
        const currentSale = Number(mCurrentSaleValue || 0);
        const orderStatus = (mOrderStatus || "").toString();

        let mTCSAmount = 0; // VB's mTCSAmount

        if (Boolean(bTCSApplicable)) {
            // default TCS base = rounded net amount (netAmountWithRounding computed earlier)
            let TCSOnValue = netAmountWithRounding;

            if (manualTCS) {
                // Total manual control: use UI-entered rate (oTCSRate)
                mTCSAmount = Math.round((TCSOnValue * oTCSRate) / 100); // rounded to 0 decimals

            } else if (partyControlFlag) {
                // Controlled by Party Master: use mTCSRate if party is applicable, otherwise tmp rate = 0
                if (partyTCSApplicable) {
                    // show rate mTCSRate in UI (if you want to set a textbox/state)
                    // e.g. setTcsRateDisplay(Number(mTCSRate).toFixed(2))
                    mTCSAmount = Math.round((TCSOnValue * Number(mTCSRate || 0)) / 100);
                } else {
                    const tmpTCSRate = 0;
                    // setTcsRateDisplay(tmpTCSRate.toFixed(2))
                    mTCSAmount = Math.round((TCSOnValue * tmpTCSRate) / 100);
                }
            } else {
                // Auto TCS Calculation — based on cumulative sales threshold
                let CurrentTotalSaleValue = currentSale + totalTaxableAmount; // totalTaxableAmount computed earlier
                mTCSAmount = 0;

                // Only calculate if CurrentTotalSaleValue > threshold and order not already Invoiced
                if (CurrentTotalSaleValue > Number(mTCSThresoldAmount || 0) &&
                    orderStatus.toLowerCase() !== "invoiced") {

                    if (currentSale < Number(mTCSThresoldAmount || 0)) {
                        // Consider only the portion above threshold
                        TCSOnValue = currentSale + netAmountWithRounding - Number(mTCSThresoldAmount || 0);
                    }

                    // show rate mTCSRate in UI: setTcsRateDisplay(Number(mTCSRate).toFixed(2))
                    mTCSAmount = Math.round((TCSOnValue * Number(mTCSRate || 0)) / 100);
                }
            }
        }

        // expose TCS results in variables used later
        tcsAmount = Number(mTCSAmount || 0);

        // add TCS to net amount (same behavior as VB: add mTCSAmount after rounding)
        netAmountWithRounding = Number(netAmountWithRounding || 0) + tcsAmount;

        // list out summary details
        const totalSummary = {
            TotalQty: totalQty,
            totalAmountExclDiscount: totalAmountExclDiscount,
            totalDiscount: totalDiscount,
            totalAmountAfterDiscount: totalAmountAfterDiscount,
            freightAmount: freightAmount,
            insuranceAmount: insuranceAmount,
            serviceChargeAmount: serviceChargeAmount,
            totalTaxableAmount: totalTaxableAmount,
            totalGSTAmount: totalGSTAmount,
            totalCessAmount: totalCessAmount,
            roundOff: roundOff,
            tcsAmount: tcsAmount,
            netAmountWithRounding: netAmountWithRounding,
            salesAmount: salesAmount,
            cgstRate: cgstRate,
            sgstRate: sgstRate,
            igstRate: igstRate,
            discountRate: discountValue,
            discountType: discountType,
            freightRate: freightRate,
            insurancePercentage: insuranceRate,
            serviceCharge: serviceChargeRate,
            tcsPercentage: tcsRate,
            igstAmount: igstAmount,
            cgstAmount: cgstAmount,
            sgstAmount: sgstAmount,
            cessRate: cessRate,

        };


        setTotalInvoice(totalSummary);

        setrowsConfirmedOrdersDespacth(rows);

    }

    const LoadInvoiceOrderCombinedInvoicePrint = async (params) => {

        const requestBody = {
            IParentInvoiceOrder_ID: params.InvoicePlanNo,
            ConstructedGlobalValues: {
                COMPANY_FINANCIAL_YEAR_ID: FinancierYear,
                COMPANY_ID: localStorage.getItem('companyid'),
                LOGIN_USER: localStorage.getItem('loggedinuser'),
            },
        };

        fetch(API_ENDPOINTS.LoadInvoiceOrder_Combined_Invoice_Print, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(requestBody)
        })
            .then(response => {
                if (!response.ok) {
                    throw new Error('Unable to load invoice order for update');
                }
                return response.json();
            })
            .then(data => {

                const fdata = data.SSalesOrderItems.map((row, index) => {
                    const SShipmentPlanDate = new Date(row.SShipmentPlanDate).toLocaleDateString();
                    return { ...row, id: index + 1, SShipmentPlanDate };
                });

                setrowsConfirmedOrdersDespacth(fdata);

                handleOrderItemCalculation(data, fdata);

            })
            .catch(error => {

                showError('Unable to load invoice order for update', 'error');
            });
    }


    const handleViewReport = async (printmode, IParentInvoiceOrder_ID) => {

        //invoice load 
        try {
           LoadInvoiceOrderCombinedInvoicePrint({ InvoicePlanNo: IParentInvoiceOrder_ID });

            if (printmode === 'invoice') {

                //openReportWindow(InvoiceReport, "Invoice")
                try {
                    if (!IParentInvoiceOrder_ID || IParentInvoiceOrder_ID === 0) {
                        showError('Invalid Parent Invoice Order ID!', 'warning');
                        return;
                    }

                    const requestBody = {
                        IParentInvoiceOrder_ID: IParentInvoiceOrder_ID,
                        COMPANY_FINANCIAL_YEAR_ID: FinancierYear,
                        COMPANY_ID: localStorage.getItem('companyid'),
                        ConstructedGlobalValues: {
                            COMPANY_FINANCIAL_YEAR_ID: FinancierYear,
                            COMPANY_ID: localStorage.getItem('companyid'),
                        },
                    };

                    const response = await callApi(
                        API_ENDPOINTS.REPORT_INVOICE_ALL_COPIES,
                        'POST',
                        { 'Content-Type': 'application/json' },
                        requestBody
                    );

                    if (response[0].invoice_detail && response[0].invoice_detail.length > 0) {
                        if (response && response.length > 0) {
                            setInvoiceList(response);

                            setViewInvoiceReport(true);
                            setInvoiceDetail(false);
                            setViewPackingList(false);
                        }

                    } else {
                        showError('No data found for the selected Invoice Order!', 'info');
                    }
                } catch (error) {
                    console.error('Error loading packing list:', error);
                    showError('Failed to generate packing list.', 'error');
                }


            }
            else if (printmode === 'packinglist') {
                try {
                    if (!IParentInvoiceOrder_ID || IParentInvoiceOrder_ID === 0) {
                        showError('Invalid Parent Invoice Order ID!', 'warning');
                        return;
                    }

                    const requestBody = {
                        IParentInvoiceOrder_ID: IParentInvoiceOrder_ID,
                        COMPANY_FINANCIAL_YEAR_ID: FinancierYear,
                        COMPANY_ID: localStorage.getItem('companyid'),
                        ConstructedGlobalValues: {
                            COMPANY_FINANCIAL_YEAR_ID: FinancierYear,
                            COMPANY_ID: localStorage.getItem('companyid'),
                        },
                    };

                    const response = await callApi(
                        API_ENDPOINTS.REPORT_INVOICE_PACKINGLIST,
                        'POST',
                        { 'Content-Type': 'application/json' },
                        requestBody
                    );

                    if (response && response.length > 0) {
                        setPackingList(response);

                        setViewPackingList(true);
                        setInvoiceDetail(false);

                    } else {
                        showError('No data found for the selected Invoice Order!', 'info');
                    }
                } catch (error) {
                    console.error('Error loading packing list:', error);
                    showError('Failed to generate packing list.', 'error');
                }


            }

            else if (printmode === 'packinglistexport') {
                //openReportWindow(PackingListExcelDownload, "Invoice")
                handleInvoiceExport(IParentInvoiceOrder_ID);
            }

        } catch (error) {
            console.error('Error generating report:', error);
            // Handle error
        }

    };


    const handlePackinglistcallback = () => {
        setInvoiceDetail(true);
        setViewPackingList(false);
        setViewInvoiceReport(false);
    }
 const handleInvoicelistcallback = () => {
        setInvoiceDetail(true);
        setViewPackingList(false);
        setViewInvoiceReport(false);
    }


    return (
        <Box>

            {viewInvoiceDetail && (
                <>
                    {mode === 'list' && (
                        <>
                            <AppBar position="static" sx={{ backgroundColor: "inherit", borderRadius: "0px", height: "48px", justifyContent: "center" }}>
                                <Toolbar>
                                    {/* First Menu */}
                                    <Button
                                        color="secondary"
                                        onClick={handleMenuClickFirst}
                                        endIcon={<ExpandMoreIcon sx={{ color: "teal", fontSize: "18px" }} />}
                                        sx={{ textTransform: 'none', fontSize: "16px", fontWeight: "500" }}
                                    >
                                        {selectedOptionFirst}
                                    </Button>
                                    <Menu
                                        anchorEl={anchorElFirst}
                                        open={openFirst}
                                        onClose={handleMenuCloseFirst}
                                    >
                                        <MenuItem onClick={() => handleMenuItemClickFirst('All Invoice')}>All Invoice</MenuItem>
                                        <MenuItem onClick={() => handleMenuItemClickFirst('New')}>New</MenuItem>
                                        <MenuItem onClick={() => handleMenuItemClickFirst('Approved')}>Approved</MenuItem>
                                        <MenuItem onClick={() => handleMenuItemClickFirst('Cancelled')}>Cancelled</MenuItem>
                                        <MenuItem onClick={() => handleMenuItemClickFirst('Under Amendment')}>Under Amendment</MenuItem>
                                        <MenuItem onClick={() => handleMenuItemClickFirst('Processed')}>Processed</MenuItem>
                                        <MenuItem onClick={() => handleMenuItemClickFirst('Invoiced')}>Invoiced</MenuItem>
                                    </Menu>

                                    {/* Adding margin-left: auto to push the button to the right */}
                                    <Tooltip title="Create Invoice" >
                                        <Button size="small" variant="contained" onClick={handleCreateDispatchPlans} color="secondary" startIcon={<AddIcon />} sx={{ marginLeft: 'auto', textTransform: 'none' }}>New</Button>
                                    </Tooltip>

                                    {/* Second Menu */}
                                    <IconButton
                                        size="small"
                                        aria-label="menu"
                                        sx={{ ml: 2 }}
                                        onClick={handleMenuClickSecond}
                                    >
                                        <MoreVertIcon />
                                    </IconButton>

                                    <Menu
                                        anchorEl={anchorElSecond}
                                        open={openSecond}
                                        onClose={handleMenuCloseSecond}
                                    >
                                        <MenuItem onClick={() => handleMenuItemClickSecond('Import Items')}>
                                            <ListItemIcon>
                                                <ImportExportIcon />
                                            </ListItemIcon>
                                            <ListItemText primary="Import Items" />
                                        </MenuItem>
                                        <MenuItem onClick={() => handleMenuItemClickSecond('Export Items')}>
                                            <ListItemIcon>
                                                <FileDownloadIcon />
                                            </ListItemIcon>
                                            <ListItemText primary="Export Items" />
                                        </MenuItem>
                                        <Divider />
                                        <MenuItem onClick={() => handleMenuItemClickSecond('Export Current View')}>
                                            <ListItemIcon>
                                                <ViewColumnIcon />
                                            </ListItemIcon>
                                            <ListItemText primary="Export Current View" />
                                        </MenuItem>
                                        <Divider />
                                        <MenuItem onClick={() => handleMenuItemClickSecond('Preference List')}>
                                            <ListItemIcon>
                                                <ListAltIcon />
                                            </ListItemIcon>
                                            <ListItemText primary="Preference " />
                                        </MenuItem>
                                        <Divider />
                                        <MenuItem onClick={() => handleMenuItemClickSecond('Refresh List')}>
                                            <ListItemIcon>
                                                <RefreshIcon />
                                            </ListItemIcon>
                                            <ListItemText primary="Refresh List" />
                                        </MenuItem>
                                        <Divider />
                                        <MenuItem onClick={() => handleMenuItemClickSecond('Reset Column Width')}>
                                            <Button variant="contained" color="secondary" size="small" startIcon={<FormatAlignJustifyIcon />}>
                                                Reset Columns Width
                                            </Button>
                                        </MenuItem>
                                    </Menu>

                                    <IconButton
                                        size="small"
                                        aria-label="menu"
                                        sx={{ mr: 1, ml: 1 }}
                                        onClick={handleMenuClickSecond}
                                    >
                                        <HelpIcon />
                                    </IconButton>

                                    <div>
                                        <IconButton>
                                            <Close fontSize="inherit" style={{ color: "#2196F3" }} onClick={handleBackDashboard} />
                                        </IconButton>
                                    </div>
                                </Toolbar>
                            </AppBar>
                            <CustomSnackbar open={snackbarOpen} message={alertMessage} onClose={handleSnackbarClose} type={alertsevierity} />
                            <Box>
                                {/* <DateRangePicker fromDate={fromData} toDate={toDate} onFromChange={(date) => handleDateChange(date, 'from')} onToChange={(date) => handleDateChange(date, 'to')} autoFocus /> */}
                                <DateRangePickerForInvoice
                                    fromDate={fromData}
                                    toDate={toDate}
                                    onFromChange={(date) => handleDateChange(date, "from")}
                                    onToChange={(date) => handleDateChange(date, "to")}
                                    autoFocus
                                    searchType={searchType}
                                    searchValue={searchValue}
                                    onSearchTypeChange={(e) => setSearchType(e.target.value)}
                                    onSearchValueChange={(e) => setSearchValue(e.target.value)}
                                    onSearchClick={onSearchClick}
                                />

                                <ResponsiveBox colors={colors} customMaxHeight={{ xs: '72vh', sm: '72vh', md: '74vh', lg: '85vh', xl: '100vh' }}>
                                    <CustomTableContainer colors={colors}>
                                        <TableContainer >
                                            <Table size="small">
                                                <TableHead sx={{ height: 30, backgroundColor: colors.primary[400] }}>
                                                    <TableRow>
                                                        <TableCell>Action</TableCell>
                                                        <TableCell>Invoice No</TableCell>
                                                        <TableCell>Invoice Date</TableCell>
                                                        <TableCell>Invoice Status</TableCell>
                                                        <TableCell>Bill-To Party</TableCell>
                                                        <TableCell>Ship-To Party</TableCell>
                                                        <TableCell>Ship-To City</TableCell>
                                                        <TableCell>Ship-To State</TableCell>
                                                        <TableCell>CO No</TableCell>
                                                        <TableCell>Total Qty</TableCell>
                                                        <TableCell>DO No</TableCell>
                                                        <TableCell>DO Date</TableCell>
                                                        <TableCell>Invoice Amount</TableCell>
                                                        <TableCell>IRN No</TableCell>
                                                        <TableCell>Invoice Plan No</TableCell>
                                                        <TableCell></TableCell>

                                                    </TableRow>

                                                    <TableFilterRow
                                                        columns={columns}
                                                        filters={filters}
                                                        handleFilterChange={handleFilterChange}
                                                        uniqueValues={uniqueValues}
                                                        autoCompleteColumns={autoCompleteColumns}
                                                    />
                                                </TableHead>
                                                <TableBody>
                                                    {currentRecords.map((row, index) => (
                                                        <TableRow key={row.DespatchOrderId} sx={{
                                                            backgroundColor:
                                                                index % 2 === 0
                                                                    ? theme.palette.background.default
                                                                    : alpha(theme.palette.action.hover, 0.05), // slightly darker
                                                        }}>
                                                            <TableCell>
                                                                <Tooltip title="Edit Invoice">
                                                                    <Edit
                                                                        onClick={() => handleCallEdit(row)}
                                                                        style={{
                                                                            color: "#1E88E5",
                                                                            fontSize: "20px",
                                                                            cursor: "pointer",
                                                                        }}
                                                                    />
                                                                </Tooltip>
                                                            </TableCell>

                                                            <TableCell>
                                                                {row.InvoiceNo}
                                                            </TableCell>

                                                            <TableCell>
                                                                {row.InvoiceDate
                                                                    ? dayjs(row.InvoiceDate).format('DD-MMM-YYYY')
                                                                    : ''}
                                                            </TableCell>
                                                            <TableCell>{row.InvoiceStatus}</TableCell>
                                                            <TableCell>{row.BillToCustomer}</TableCell>
                                                            <TableCell>{row.ShipToCustomer}</TableCell>
                                                            <TableCell>{row.ShipToCity}</TableCell>
                                                            <TableCell>{row.ShipToState}</TableCell>
                                                            <TableCell>{row.ConfirmationNo}</TableCell>
                                                            <TableCell sx={{ textAlign: 'right' }}>{row.TotalQty}</TableCell>
                                                            <TableCell>{row.ShippedOrderNo}</TableCell>
                                                            <TableCell>
                                                                {row.ShippedOrderDate
                                                                    ? dayjs(row.ShippedOrderDate).format('DD-MMM-YYYY')
                                                                    : ''}
                                                            </TableCell>
                                                            <TableCell sx={{ textAlign: 'right' }}>{row.NetAmount}</TableCell>
                                                            <TableCell sx={{ textAlign: 'right' }}>{row.IRNNo}</TableCell>
                                                            <TableCell sx={{ textAlign: 'right' }}>{row.InvoicePlanNo}</TableCell>


                                                            <TableCell sx={{ display: 'flex', gap: '5px', justifyContent: 'flex-end' }}>
                                                                {row.InvoiceStatus === "Invoiced" && (

                                                                    <>
                                                                        <Tooltip title="Print Invoice">
                                                                            <PrintOutlined
                                                                                onClick={() => handleViewReport("invoice", row.InvoicePlanNo)}
                                                                                sx={{ fontSize: "23px", cursor: "pointer", color: "#0288d1" }}
                                                                            />
                                                                        </Tooltip>

                                                                        <Tooltip title="Print Packing List">
                                                                            <DescriptionOutlined
                                                                                onClick={() => handleViewReport("packinglist", row.InvoicePlanNo)}
                                                                                sx={{ fontSize: "23px", cursor: "pointer", color: "#00796b" }}
                                                                            />
                                                                        </Tooltip>

                                                                        <Tooltip title="Export Packing List">
                                                                            <FileDownloadOutlined
                                                                                onClick={() => handleViewReport("packinglistexport", row.InvoicePlanNo)}
                                                                                sx={{ fontSize: "23px", cursor: "pointer", color: "#ff8f00" }}
                                                                            />
                                                                        </Tooltip>

                                                                    </>
                                                                )}

                                                            </TableCell>



                                                        </TableRow>
                                                    ))}

                                                    <TableRow sx={{ backgroundColor: colors.primary[400], height: 10 }}>
                                                        <TableCell colSpan={9} sx={{ fontWeight: 'bold', textAlign: 'right' }}>TOTAL </TableCell>
                                                        <TableCell sx={{ textAlign: "right", fontWeight: 'bold' }}>{totals.TotalQty.toFixed(1)}</TableCell>
                                                        <TableCell /> {/* SIZE1 */}
                                                        <TableCell /> {/* SIZE2 */}
                                                        <TableCell sx={{ textAlign: "right", fontWeight: 'bold' }}>{totals.NetAmount.toFixed(1)}</TableCell>
                                                        <TableCell /> {/* REMARKS */}
                                                        <TableCell /> {/* GRADE */}
                                                        <TableCell /> {/* GRADE */}
                                                    </TableRow>
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </CustomTableContainer>
                                </ResponsiveBox>


                                <CustomPagination pageCount={pageCount} currentPage={currentPage} onPageChange={handlePageChange} />
                            </Box>

                        </>
                    )}

                    {mode === 'create' && (<>
                        <CreateNewInvoice handleBackWinderMachineEntry={handleCallBack} editRow={selectedRows} />
                    </>)}

                </>)}


            {viewPackingList && (<> <PackingListReport apiData={PackingList} callback={handlePackinglistcallback} /> </>)}
            {viewInvoiceReport && (<> <InvoiceReport apiData={InvoiceList} callback={handleInvoicelistcallback} orderItems={rowsConfirmedOrdersDespacth} totals={totalInvoice} /> </>)}


        </Box>

    );
};

export default InvoiceList;